import { CommonModule } from '@angular/common'
import { Component, Input, OnInit } from '@angular/core'
import { RouterModule } from '@angular/router'
import { NzIconModule } from 'ng-zorro-antd/icon'
import { forkJoin } from 'rxjs'
import { AtomModule } from '../../atom/atom.module'
import { Asset, GroupValue, Product, SearchResult } from '../../gql/generated'
import { AcfService } from '../../service/acf.service'
import { AppService } from '../../service/app.service'
import { AssetService } from '../../service/asset.service'
import { ProductService } from '../../service/product.service'

@Component({
  selector: 'app-game-card-alpha',
  templateUrl: './game-card-alpha.component.html',
  styleUrl: './game-card-alpha.component.less',
  standalone: true,
  imports: [AtomModule, NzIconModule, CommonModule, RouterModule],
})
export class GameCardAlphaComponent implements OnInit {
  @Input() data!: SearchResult
  product: Product

  developer = ''
  publisher = ''
  releaseDate = ''
  videoAssset: Asset
  mainAsset: Asset = {} as Asset
  discountPercentage = 10

  platforms = [
    { label: 'Nintendo', icon: 'ng-zorro:nintendo' },
    { label: 'Playstation', icon: 'ng-zorro:playstation' },
    { label: 'Xbox', icon: 'ng-zorro:xbox' },
    { label: 'PC', icon: 'ng-zorro:pc' },
  ]

  constructor(
    private productService: ProductService,
    private acfService: AcfService,
    private assetService: AssetService,
    private appService: AppService
  ) {}

  ngOnInit(): void {
    this.fetch()
  }

  fetch(): void {
    const entityName = 'Product'
    const entityId = this.data.productId

    if (!this.data || !this.data.productId) {
      return
    }

    // Initial observables to collect data
    const product$ = this.productService.fetchProduct(this.data.slug)

    const gameInformation$ = this.acfService.getGroupValue({
      entityId,
      entityName,
      fieldName: 'gameInformation',
      template: this.appService.appStore$.getValue().template,
    })

    const gameCardContent$ = this.acfService.getGroupValue({
      entityId,
      entityName,
      fieldName: 'gameCardContent',
      template: this.appService.appStore$.getValue().template,
    })

    // Run all forkJoins in parallel to avoid sequential dependencies
    forkJoin({
      product: product$,
      gameInformation: gameInformation$,
      gameCardContent: gameCardContent$,
      // Remove the asset pipes, we'll handle them below to avoid undefined errors
    }).subscribe((results) => {
      this.product = results.product as Product

      // Extract information from game information
      if (results.gameInformation) {
        this.developer =
          (this.acfService.getGroupMemberValueByFieldId(
            results.gameInformation as GroupValue,
            'gameInformationDeveloper'
          ) as string) || ''

        this.publisher =
          (this.acfService.getGroupMemberValueByFieldId(
            results.gameInformation as GroupValue,
            'gameInformationPublisher'
          ) as string) || ''

        this.releaseDate =
          (this.acfService.getGroupMemberValueByFieldId(
            results.gameInformation as GroupValue,
            'gameInformationReleaseDate'
          ) as string) || ''
      }

      // Extract information from game card content
      if (results.gameCardContent) {
        this.discountPercentage =
          (this.acfService.getGroupMemberValueByFieldId(
            results.gameCardContent as GroupValue,
            'discountPercentage'
          ) as number) || 0

        const videoAssetId = this.acfService.getGroupMemberValueByFieldId(
          results.gameCardContent as GroupValue,
          'videoAsset'
        )

        const mainAssetId = this.acfService.getGroupMemberValueByFieldId(
          results.gameCardContent as GroupValue,
          'mainAsset'
        )

        // Now fetch the assets in parallel
        if (mainAssetId || videoAssetId) {
          forkJoin({
            mainAsset: mainAssetId
              ? this.assetService.getAsset(mainAssetId as string)
              : [],
            videoAsset: videoAssetId
              ? this.assetService.getAsset(videoAssetId as string)
              : [],
          }).subscribe((assetResponse) => {
            if (assetResponse.mainAsset) {
              this.mainAsset = assetResponse.mainAsset as Asset
            }
            if (assetResponse.videoAsset) {
              this.videoAssset = assetResponse.videoAsset as Asset
            }
          })
        }
      }
    })
  }
}
